import { Field } from '@progress/kendo-react-form';
import React from 'react';
import { AddButton } from 'shared/buttons';
import BaseGrid from 'shared/grids/BaseGrid';
import { BaseCell } from 'shared/grids/Cells';
import FormActionsCell from 'shared/grids/FormGrid/FormActionsCell';
import { Box } from 'shared/layout/Box';
import { emptyObject } from 'src/shared/helpers/utils';

export const fallbackIdentifierColumn = '_key';

const defaultEditField = '_isInEdit';
export const editField = defaultEditField;
export const ActionBarContent = ({ defaultRowValue, value, onChange, columns = [], editField, ...props }) => {
  const handleChange = () => {
    const defaultValue = {
      [fallbackIdentifierColumn]: `key_${ Date.now()}`,
      [editField]: true,
      ...defaultRowValue,
    };
    columns.forEach((column) => {
      if (column.defaultValue) {
        defaultValue[column.field] = column.defaultValue;
      }
    });
    onChange({ value: [defaultValue, ...(value ?? [])] });
  };
  return (
    <>
      <AddButton {...props} onClick={handleChange} />
      {props.actionBarContent}
    </>
  );
};

// Use this to only have a delete button in the actions cell
export const NewEntityActionCellProps = { hideEditButton: true, defaultInEdit: true, confirmDelete: false };

const defaultActionCellProps = { itemName: 'this item' };

// a pure version of FormGrid to lower complexity of nested array of objects
// FormGrid has consistanly caused bugs when nesting
export const GridInput = (props) => {
  const {
    value,
    onChange,
    columns: columnProp,
    Cell = BaseCell,
    ActionsCell = FormActionsCell,
    ActionCellProps = defaultActionCellProps,
    GridActions = ActionBarContent,
    GridActionsProps = emptyObject,
    multiEdit = true,
    editField = defaultEditField,
  } = props;

  const valueRef = React.useRef(value);
  valueRef.current = value;

  const isEditing = value?.some((x) => x[editField]);

  const columns = React.useMemo(() => {
    const cols = [
      ...columnProp.map((column, i) => {
        const { cell } = column;
        return {
          ...column,
          cell: (p) => {
            const field = `${props.name}[${p.dataIndex}].${column.field}`;
            const handleChange = (e) => {
              const newValue = [...(valueRef.current ?? [])];
              newValue[p.dataIndex][column.field] = e.value;
              onChange({ value: newValue });
            };
            return (
              <Cell>
                <Box marginTop="-16px" paddingBottom="8px">
                  <Field
                    {...p}
                    {...column}
                    id={field}
                    name={field}
                    component={cell}
                    onChange={handleChange}
                    disabled={p.disabled || !p.dataItem[editField]}
                  />
                </Box>
              </Cell>
            );
          },
        };
      }),
    ];

    if (ActionsCell) {
      cols.push({
        title: 'Actions',
        isSystem: true,
        sortable: false,
        width: ActionCellProps?.style?.width ?? '100px',
        cell: (p) => (
          <ActionsCell
            {...p}
            inEdit={p.dataItem[editField]}
            editField={editField}
            {...ActionCellProps}
            isEditing={valueRef.current?.some((x) => x[editField])}
            onCancel={() => {
              const newValue = [...(valueRef.current ?? [])];
              newValue[p.dataIndex][editField] = false;
              onChange({ value: newValue });
              ActionCellProps.onCancel?.(newValue[p.dataIndex], p.dataIndex);
            }}
            onDelete={async () => {
              try {
                await ActionCellProps.onDelete?.(p.dataItem);
                onChange({
                  value: valueRef.current.filter((_, i) => i !== p.dataIndex),
                });
              } catch (error) {
                console.error(error);
              }
            }}
            onEnterEdit={(dataItem) => {
              const newValue = [...(valueRef.current ?? [])];
              newValue[p.dataIndex][editField] = true;
              onChange({ value: newValue });
              ActionCellProps.onEnterEdit?.(dataItem);
            }}
          />
        ),
      });
    }

    return cols;
  }, [columnProp, ActionsCell]);

  return (
    <Box display="flex" flexDirection="column">
      <BaseGrid
        autoClientSidePagination
        {...props}
        hideActionsCell
        data={value ?? []}
        columns={columns}
        actionBarContent={<GridActions disabled={!multiEdit && isEditing} {...props} {...GridActionsProps} />}
      />
    </Box>
  );
};

// if multiEdit is true, the grid will allow multiple rows to be edited at once
// have a badly built api response that requires this since even though it affect a single sub entity, the entire parent entiiy is returned
// So figuring the intended id is ambiguos so we update all the rows at once and only allow single edit at a time
export const SingleEditGridInput = (props) => <GridInput {...props} multiEdit={false} />;